import client from '../api/client.js'

/**
 * CONTRACT: ticket_price is ALWAYS a string (decimal-safe).
 * Backend returns it as string; frontend must NEVER coerce it to number.
 * This guard is a safety net — it logs a warning during development
 * if a payload arrives with a numeric ticket_price.
 */
const assertTicketPriceSafe = (data) => {
  if (!import.meta.env?.DEV) return
  const price = data?.ticket_price
  if (price !== undefined && typeof price !== 'string') {
    console.warn('[lotteryService] ticket_price CONTRACT VIOLATION — expected string, got', typeof price, price)
  }
}

const mapLotteryOut = (value) => {
  if (!value || typeof value !== 'object') return value
  const ticketPrice = value?.ticket_price ?? value?.price_per_ticket ?? value?.unit_price
  return {
    ...value,
    ...(ticketPrice !== undefined && ticketPrice !== null ? { ticket_price: String(ticketPrice) } : {}),
  }
}

const resolveOrgId = (orgId) => {
  if (!orgId) {
    if (import.meta.env.DEV) {
      console.warn('No activeOrgId set')
    }
    throw new Error('Missing activeOrgId')
  }

  return orgId
}

const orgConfig = (orgId, options = {}) => {
  const resolvedOrgId = resolveOrgId(orgId)

  const subOrgId = options?.subOrgId
  const hasSubOrg = subOrgId !== null && subOrgId !== undefined && subOrgId !== ''

  return {
    headers: {
      'X-Org-Id': String(resolvedOrgId),
    },
    ...(hasSubOrg ? { params: { sub_org_id: Number(subOrgId) } } : {}),
  }
}

const orgPostConfig = (orgId) => {
  const resolvedOrgId = resolveOrgId(orgId)

  return {
    headers: {
      'X-Org-Id': String(resolvedOrgId),
    },
  }
}

export const fetchLotteriesRequest = async (orgId, subOrgId = null) => {
  const response = await client.get('/lotteries/', orgConfig(orgId, { subOrgId }))
  const data = response?.data
  const mapped = Array.isArray(data) ? data.map(mapLotteryOut) : data
  if (Array.isArray(mapped)) mapped.forEach(assertTicketPriceSafe)
  return mapped
}

export const fetchLotteryDetailRequest = async (lotteryId, orgId) => {
  try {
    const response = await client.get(`/lotteries/${lotteryId}`, orgConfig(orgId))
    const data = mapLotteryOut(response?.data)
    assertTicketPriceSafe(data)
    return data
  } catch (err) {
    const status = Number(err?.response?.status || 0)

    // Keep auth handling in axios interceptor path.
    if (status === 401) {
      throw err
    }

    if (status === 404 || status === 405) {
      const controlledError = new Error('Lottery detail endpoint is temporarily unavailable.')
      controlledError.code = 'LOTTERY_DETAIL_UNAVAILABLE'
      controlledError.status = status
      controlledError.cause = err
      throw controlledError
    }

    throw err
  }
}

export const createLotteryRequest = async (payload, orgId) => {
  const response = await client.post('/lotteries/', payload, orgPostConfig(orgId))
  return mapLotteryOut(response?.data)
}

export const pauseLotteryRequest = async (lotteryId, orgId) => {
  const response = await client.post(`/lotteries/${lotteryId}/pause`, undefined, orgPostConfig(orgId))
  return response?.data
}

export const resumeLotteryRequest = async (lotteryId, orgId) => {
  const response = await client.post(`/lotteries/${lotteryId}/resume`, undefined, orgPostConfig(orgId))
  return response?.data
}

export const closeLotteryRequest = async (lotteryId, orgId) => {
  const response = await client.post(`/lotteries/${lotteryId}/close`, undefined, orgPostConfig(orgId))
  return response?.data
}

export const archiveLotteryRequest = async (lotteryId, orgId) => {
  const response = await client.post(`/lotteries/${lotteryId}/archive`, undefined, orgPostConfig(orgId))
  return response?.data
}

export const updateLotteryRequest = async (lotteryId, payload, orgId) => {
  const response = await client.patch(`/lotteries/${lotteryId}`, payload, orgPostConfig(orgId))
  return response?.data
}

export const fetchLotteryAssignmentsRequest = async (id, orgId) => {
  const response = await client.get(`/lotteries/${id}/assignments`, orgConfig(orgId))

  return response?.data
}

export const fetchLotteryBatchesRequest = async (id, orgId) => {
  const response = await client.get(`/lotteries/${id}/batches`, orgConfig(orgId))

  return response?.data
}

export const uploadTicketBatchRequest = async (id, formData, orgId) => {
  await client.post(`/lotteries/${id}/ticket-batches`, formData, orgPostConfig(orgId))
}

export const drawWinnerRequest = async (id, orgId) => {
  const response = await client.post(`/draw/${id}`, undefined, orgPostConfig(orgId))

  return response?.data
}

export const fetchDrawHistoryRequest = async (id, orgId) => {
  const response = await client.get(`/draw/${id}/history`, orgConfig(orgId))

  return response?.data
}

export const fetchLotteryPrizesRequest = async (lotteryId, orgId) => {
  const response = await client.get(`/lottery-prizes/${lotteryId}`, orgConfig(orgId))
  return response?.data
}

export const createLotteryPrizeRequest = async (lotteryId, payload, orgId) => {
  const response = await client.post(`/lottery-prizes/${lotteryId}`, payload, orgPostConfig(orgId))
  return response?.data
}

export const updateLotteryPrizeRequest = async (prizeId, payload, orgId) => {
  const response = await client.put(`/lottery-prizes/${prizeId}`, payload, orgPostConfig(orgId))
  return response?.data
}

export const deleteLotteryPrizeRequest = async (prizeId, orgId) => {
  const response = await client.delete(`/lottery-prizes/${prizeId}`, orgPostConfig(orgId))
  return response?.data
}

export const fetchLotteryReportRequest = async (lotteryId, snapshotId, orgId) => {
  const config = orgConfig(orgId)
  if (snapshotId !== null && snapshotId !== undefined && String(snapshotId).trim() !== '') {
    config.params = {
      ...(config.params || {}),
      snapshot_id: String(snapshotId).trim(),
    }
  }

  const response = await client.get(`/reports/lotteries/${lotteryId}`, config)
  return response?.data
}

export const downloadWinnersCsvRequest = async (lotteryId, orgId) => {
  const response = await client.get(`/report-exports/lotteries/${lotteryId}/winners.csv`, {
    ...orgConfig(orgId),
    responseType: 'blob',
  })
  return response?.data
}

export const fetchLotteryWinnersRequest = async (lotteryId, orgId) => {
  const response = await client.get(`/lottery-winners/${lotteryId}`, orgConfig(orgId))
  return response?.data
}


export const publishLotteryWinnersRequest = async (lotteryId, orgId) => {
  const response = await client.post(`/lottery-winners/${lotteryId}/publish`, undefined, orgPostConfig(orgId))
  return response?.data
}

export const fetchBatchDetailRequest = async (lotteryId, batchId, orgId) => {
  const response = await client.get(`/lotteries/${lotteryId}/batches/${batchId}`, orgConfig(orgId))

  return response?.data
}

export const fetchBatchImageRequest = async (lotteryId, batchId, orgId) => {
  const response = await client.get(`/lotteries/${lotteryId}/batches/${batchId}/image`, {
    ...orgConfig(orgId),
    responseType: 'blob',
  })

  return response?.data
}

export const fetchMyBatchesRequest = async (lotteryId, orgId) => {
  const response = await client.get(`/lotteries/${lotteryId}/my-batches`, orgConfig(orgId))

  return response?.data
}

export const fetchUserBatchesRequest = async (lotteryId, userId, orgId) => {
  const response = await client.get(`/lotteries/${lotteryId}/user/${userId}/batches`, orgConfig(orgId))

  return response?.data
}

export const fetchBatchByTicketRequest = async (lotteryId, ticketNumber, orgId) => {
  const response = await client.get(`/lotteries/${lotteryId}/batches/by-ticket/${ticketNumber}`, orgConfig(orgId))

  return response?.data
}

export const assignUsersToLotteryRequest = async (lotteryId, userIds, orgId) => {
  const response = await client.post(
    `/lotteries/${lotteryId}/assign-users`,
    { user_ids: userIds },
    orgPostConfig(orgId),
  )

  return response?.data
}

export const assignManagersToLotteryRequest = async (lotteryId, userIds, orgId) => {
  const response = await client.post(
    `/lotteries/${lotteryId}/assign-managers`,
    { user_ids: userIds },
    orgPostConfig(orgId),
  )

  return response?.data
}

export const fetchLotteryAttachments = async (lotteryId) => {
  const response = await client.get(`/lotteries/${lotteryId}/attachments`)
  return response?.data
}

export const uploadLotteryAttachment = async (lotteryId, file, replaceExisting = false) => {
  const formData = new FormData()
  formData.append('file', file)
  formData.append('replace_existing', String(replaceExisting === true))

  const response = await client.post(`/lotteries/${lotteryId}/attachments`, formData)
  return response?.data
}

export const downloadLotteryAttachment = async (lotteryId, attachmentId) => {
  const response = await client.get(`/lotteries/${lotteryId}/attachments/${attachmentId}/download`, {
    responseType: 'blob',
  })
  return response?.data
}

export const sendDeadlineReminder = async (lotteryId) => {
  const response = await client.post(`/lotteries/${lotteryId}/send-deadline-reminder`)
  return response?.data
}

